import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  FolderOpen,
  Sparkles,
  Images,
  Settings,
  LogOut,
  Menu,
  X,
  Vault,
  ShieldAlert,
  Scissors,
  ChevronLeft,
  ChevronRight,
  Store,
  ShoppingBag,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import ImpersonationBanner from "./ImpersonationBanner";

const IDLE_TIMEOUT = 30 * 60 * 1000;   // 30 minutes
const WARN_BEFORE  =  5 * 60 * 1000;   // warn 5 minutes before logout

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [aiEnabled, setAiEnabled] = useState(
    () => localStorage.getItem('show_api_config') === 'true'
  );

  // Inactivity timeout
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARN_BEFORE / 1000);
  const warnTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const doLogout = useCallback(async () => {
    setShowWarning(false);
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  const resetTimers = useCallback(() => {
    clearTimeout(warnTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);
    setShowWarning(false);
    setCountdown(WARN_BEFORE / 1000);

    warnTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(WARN_BEFORE / 1000);
      countdownRef.current = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
    }, IDLE_TIMEOUT - WARN_BEFORE);

    logoutTimerRef.current = setTimeout(doLogout, IDLE_TIMEOUT);
  }, [doLogout]);

  // Start timers and listen for activity
  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const onActivity = () => { if (!showWarning) resetTimers(); };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetTimers();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimeout(warnTimerRef.current);
      clearTimeout(logoutTimerRef.current);
      clearInterval(countdownRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-logout when countdown hits 0
  useEffect(() => {
    if (countdown <= 0) doLogout();
  }, [countdown, doLogout]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'show_api_config') {
        setAiEnabled(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    // Also poll for same-tab changes
    const interval = setInterval(() => {
      setAiEnabled(localStorage.getItem('show_api_config') === 'true');
    }, 500);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(interval); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isAffinity    = user?.role === "affinity";
  const isAdmin       = user?.role === "admin";
  const isPopBoutique = user?.role === "pop_boutique";
  const isPopWaitlist = user?.role === "pop_waitlist";

  const navItems = isAffinity ? [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/images", icon: Images, label: "Winners" },
  ] : isPopBoutique ? [
    { to: "/pop-boutique", icon: ShoppingBag, label: "Pop Boutique" },
    { to: "/pop-waitlist", icon: ShoppingBag, label: "Pop Waitlist" },
  ] : isPopWaitlist ? [
    { to: "/pop-waitlist", icon: ShoppingBag, label: "Pop Waitlist" },
  ] : [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    ...(isAdmin ? [{ to: "/generate", icon: Sparkles, label: "Generate", disabled: !aiEnabled }] : []),
    { to: "/bank", icon: Vault, label: "Bank" },
    { to: "/collections", icon: FolderOpen, label: "Collections" },
    { to: "/images", icon: Images, label: "Winners" },
    { to: "/knockout", icon: Scissors, label: "Knockout" },
    { to: "/retail-outlets", icon: Store, label: "Retail Outlets" },
    { to: "/rad-store", icon: ShoppingBag, label: "RAD Store" },
    { to: "/novelty-store", icon: ShoppingBag, label: "Novelty Store" },
    ...(isAdmin ? [
      { to: "/bad-manorz-store", icon: ShoppingBag, label: "Bad Manorz Store" },
      { to: "/pop-boutique", icon: ShoppingBag, label: "Pop Boutique" },
      { to: "/pop-waitlist", icon: ShoppingBag, label: "Pop Waitlist" },
    ] : []),
  ];

  const NavItem = ({ to, icon: Icon, label, onClick, disabled }) => {
    if (disabled) {
      return (
        <div
          title={!sidebarOpen ? label : undefined}
          className={`flex items-center py-3 text-sm text-gray-300 cursor-not-allowed ${sidebarOpen ? "gap-3 px-4" : "justify-center px-0"}`}
          data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
        >
          <Icon size={18} />
          {sidebarOpen && <span>{label}</span>}
        </div>
      );
    }
    return (
      <NavLink
        to={to}
        onClick={onClick}
        title={!sidebarOpen ? label : undefined}
        className={({ isActive }) =>
          `flex items-center py-3 text-sm transition-colors ${sidebarOpen ? "gap-3 px-4" : "justify-center px-0"} ${
            isActive
              ? "bg-black text-white"
              : "text-gray-700 hover:bg-gray-100"
          }`
        }
        data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
      >
        <Icon size={18} />
        {sidebarOpen && <span>{label}</span>}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="font-heading text-lg font-bold tracking-tight">T-SHIRT PM</h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`md:hidden fixed top-14 left-0 bottom-0 w-64 bg-white border-r border-gray-200 z-50 transform transition-transform ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <nav className="flex flex-col py-4">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} onClick={() => setMobileMenuOpen(false)} />
          ))}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 mt-4 border-t border-gray-200"
            data-testid="logout-btn"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </nav>
        <div className="absolute bottom-4 left-4 right-4 p-3 bg-gray-50 border border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-gray-500 uppercase tracking-widest">Logged in as</p>
            <NavLink
              to="/settings"
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `p-1 transition-colors ${isActive ? "text-black" : "text-gray-400 hover:text-black"}`
              }
            >
              <Settings size={14} />
            </NavLink>
          </div>
          <p className="text-sm font-medium truncate">{user?.name}</p>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex fixed top-0 left-0 bottom-0 flex-col bg-white border-r border-gray-200 transition-all duration-200 ${sidebarOpen ? "w-56" : "w-14"}`}>
        {/* Header / toggle */}
        <div className={`border-b border-gray-200 flex items-center ${sidebarOpen ? "p-6 justify-between" : "p-0 justify-center py-4"}`}>
          {sidebarOpen && (
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">T-SHIRT PM</h1>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Collection Manager</p>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="p-1 text-gray-400 hover:text-black transition-colors flex-shrink-0"
            title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavItem key={item.to} {...item} />
          ))}
        </nav>
        <div className={`border-t border-gray-200 ${sidebarOpen ? "p-4" : "py-4 flex flex-col items-center gap-2"}`}>
          {sidebarOpen ? (
            <>
              <div className="p-3 bg-gray-50 border border-gray-200 mb-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-gray-500 uppercase tracking-widest">User</p>
                  <NavLink
                    to="/settings"
                    className={({ isActive }) =>
                      `p-1 transition-colors ${isActive ? "text-black" : "text-gray-400 hover:text-black"}`
                    }
                    data-testid="nav-settings"
                  >
                    <Settings size={14} />
                  </NavLink>
                </div>
                <p className="text-sm font-medium truncate">{user?.name}</p>
              </div>
              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={handleLogout}
                data-testid="desktop-logout-btn"
              >
                <LogOut size={16} />
                Logout
              </Button>
            </>
          ) : (
            <>
              <NavLink
                to="/settings"
                title="Settings"
                className={({ isActive }) =>
                  `p-2 transition-colors ${isActive ? "text-black" : "text-gray-400 hover:text-black"}`
                }
                data-testid="nav-settings"
              >
                <Settings size={18} />
              </NavLink>
              <button
                onClick={handleLogout}
                title="Logout"
                className="p-2 text-gray-400 hover:text-black transition-colors"
                data-testid="desktop-logout-btn"
              >
                <LogOut size={18} />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`pt-14 md:pt-0 min-h-screen transition-all duration-200 ${sidebarOpen ? "md:ml-56" : "md:ml-14"}`}>
        <ImpersonationBanner />
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Inactivity Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white border border-gray-200 w-full max-w-sm">
            <div className="p-6 border-b border-gray-200 flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500 text-white flex items-center justify-center flex-shrink-0">
                <ShieldAlert size={18} />
              </div>
              <div>
                <h2 className="font-heading font-bold">Session Expiring</h2>
                <p className="text-xs text-gray-500">You've been inactive</p>
              </div>
            </div>
            <div className="p-6 text-center">
              <p className="text-4xl font-heading font-bold tabular-nums">
                {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, "0")}
              </p>
              <p className="text-sm text-gray-500 mt-2">You'll be logged out automatically</p>
            </div>
            <div className="p-4 border-t border-gray-200 flex gap-3">
              <Button
                onClick={resetTimers}
                className="flex-1 rounded-none bg-black text-white hover:bg-gray-800"
              >
                Stay logged in
              </Button>
              <Button
                onClick={doLogout}
                variant="outline"
                className="flex-1 rounded-none"
              >
                Log out now
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 pb-safe">
        <div className="flex justify-around py-2">
          {navItems.slice(0, 4).map((item) =>
            item.disabled ? (
              <div
                key={item.to}
                className="flex flex-col items-center py-2 px-3 text-xs text-gray-300 cursor-not-allowed"
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} />
                <span className="mt-1">{item.label}</span>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex flex-col items-center py-2 px-3 text-xs ${
                    isActive ? "text-black" : "text-gray-500"
                  }`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} />
                <span className="mt-1">{item.label}</span>
              </NavLink>
            )
          )}
        </div>
      </nav>
    </div>
  );
}
